import mime from 'mime'
import { putFile } from './put-file'
import type { useApolloClient } from '@apollo/client/react'
import type {
  CompleteChatFileUploadMutation,
  CompleteChatFileUploadMutationVariables,
} from '@/graphql/mutations/completeChatFileUploadMutation.gql.generated'
import type {
  CreateChatFileUploadIntentMutation,
  CreateChatFileUploadIntentMutationVariables,
} from '@/graphql/mutations/createChatFileUploadIntentMutation.gql.generated'
import type {
  ChatFileQuery,
  ChatFileQueryVariables,
} from '@/graphql/queries/chatFileQuery.gql.generated'
import { ChatFileStatus } from '@/graphql/schema'
import { CompleteChatFileUploadDocument } from '@/graphql/mutations/completeChatFileUploadMutation.gql.generated'
import { CreateChatFileUploadIntentDocument } from '@/graphql/mutations/createChatFileUploadIntentMutation.gql.generated'
import { RemoveChatFileDocument } from '@/graphql/mutations/removeChatFileMutation.gql.generated'
import { ChatFileDocument } from '@/graphql/queries/chatFileQuery.gql.generated'

const POLL_INTERVAL_MS = 1000
const MAX_POLLS = 180

type Client = ReturnType<typeof useApolloClient>

export class FileProcessingError extends Error {
  constructor(readonly failureCode: string) {
    super(failureCode)
  }
}

function sleep(ms: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Cancelled', 'AbortError'))

      return
    }
    const timer = setTimeout(resolve, ms)
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer)
        reject(new DOMException('Cancelled', 'AbortError'))
      },
      { once: true },
    )
  })
}

export function removeUploadedFile(client: Client, fileId?: string) {
  if (!fileId) return
  void client
    .mutate({ mutation: RemoveChatFileDocument, variables: { fileId } })
    .catch(() => undefined)
}

async function waitUntilProcessed(
  client: Client,
  fileId: string,
  signal: AbortSignal,
) {
  for (let attempt = 0; attempt < MAX_POLLS; attempt++) {
    const { data } = await client.query<ChatFileQuery, ChatFileQueryVariables>({
      query: ChatFileDocument,
      variables: { id: fileId },
      fetchPolicy: 'network-only',
    })
    const file = data?.chatFile
    if (file?.status === ChatFileStatus.Ready) return
    if (
      file?.status === ChatFileStatus.Failed ||
      file?.status === ChatFileStatus.Deleted
    )
      throw new FileProcessingError(file.failureCode ?? 'processing_failed')
    await sleep(POLL_INTERVAL_MS, signal)
  }
  throw new FileProcessingError('processing_timeout')
}

/**
 * Creates the upload, sends the bytes straight to storage, completes it and
 * waits for scanning to finish. Resolves with the ready file's id.
 */
export async function uploadAndProcess({
  client,
  ensureSession,
  source,
  signal,
  onProgress,
  onUploaded,
}: {
  client: Client
  ensureSession: () => Promise<string>
  source: File
  signal: AbortSignal
  onProgress: (percent: number) => void
  onUploaded: (fileId: string) => void
}) {
  const sessionId = await ensureSession()
  const mimeType =
    source.type || mime.getType(source.name) || 'application/octet-stream'
  const intent = await client.mutate<
    CreateChatFileUploadIntentMutation,
    CreateChatFileUploadIntentMutationVariables
  >({
    mutation: CreateChatFileUploadIntentDocument,
    variables: {
      input: { sessionId, fileName: source.name, mimeType, size: source.size },
    },
  })
  const created = intent.data?.createChatFileUploadIntent
  if (!created) throw new Error('Unable to create an upload')
  await putFile({
    url: created.uploadUrl,
    file: source,
    mimeType,
    onProgress,
    signal,
  })
  await client.mutate<
    CompleteChatFileUploadMutation,
    CompleteChatFileUploadMutationVariables
  >({
    mutation: CompleteChatFileUploadDocument,
    variables: { fileId: created.file.id },
  })
  onUploaded(created.file.id)
  await waitUntilProcessed(client, created.file.id, signal)

  return created.file.id
}
